import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminFetch } from '../../utils/adminApi';
import {
  MessageSquare, Mail, Send, CheckCircle, XCircle, AlertTriangle,
  Clock, RefreshCw, ExternalLink, Phone, Copy, ChevronDown, ChevronUp,
  Globe, FileText, Play, Zap, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminNotifications() {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [sitemapPreview, setSitemapPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Test form state
  const [testPhone, setTestPhone] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [testTemplate, setTestTemplate] = useState('welcome_message');
  const [testEmailTemplate, setTestEmailTemplate] = useState('welcome');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([fetchStatus(), fetchTemplates(), fetchLogs(), fetchSitemap()])
      .finally(() => setLoading(false));
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/notifications/status`);
      if (res.ok) setStatus(await res.json());
    } catch {}
  };

  const fetchTemplates = async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/notifications/templates`);
      if (res.ok) setTemplates(await res.json());
    } catch {}
  };

  const fetchLogs = async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/notifications/logs?limit=30`);
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs);
        setTotalLogs(data.total);
      }
    } catch {}
  };

  const fetchSitemap = async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/sitemap/preview`);
      if (res.ok) setSitemapPreview(await res.json());
    } catch {}
  };

  const sendTestWhatsApp = async () => {
    if (!testPhone) { toast.error('Enter a phone number'); return; }
    setSending(true);
    try {
      const t = templates?.whatsapp_templates?.find(t => t.campaign_name === testTemplate);
      const res = await adminFetch(`${BACKEND_URL}/api/admin/notifications/test-whatsapp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: testPhone, name: testName || 'Test User',
          campaign_name: testTemplate,
          template_params: t?.params?.map((_, i) => i === 0 ? (testName || 'Test User') : i === t.params.length - 1 ? '+91 9887068888' : `Test ${i}`) || [testName || 'Test']
        })
      });
      const data = await res.json();
      if (data.status === 'sent') toast.success('WhatsApp sent!');
      else toast.error(`Failed: ${data.error || data.status}`);
      fetchLogs();
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const sendTestEmail = async () => {
    if (!testEmail) { toast.error('Enter an email address'); return; }
    setSending(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/notifications/test-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: testEmail, name: testName || 'Test User', template: testEmailTemplate })
      });
      const data = await res.json();
      if (data.success) toast.success('Email sent!');
      else toast.error('Failed - email not configured');
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Zap },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'sitemap', label: 'Sitemap & SEO', icon: Globe },
    { id: 'logs', label: 'Activity Log', icon: Clock }
  ];

  return (
    <div data-testid="admin-notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="notifications-title">Notifications & SEO</h1>
          <p className="text-gray-500 text-sm">Manage WhatsApp, Email notifications & Sitemap</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-6 overflow-x-auto">
        <div className="flex border-b">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`notif-tab-${tab.id}`}
              className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === tab.id ? 'border-blue-600 text-blue-600 bg-blue-50/60' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <StatusCard title="WhatsApp" provider="AiSensy" enabled={status?.whatsapp?.enabled} icon={MessageSquare}
              detail={status?.whatsapp?.enabled ? 'Connected & ready' : 'API key not set'} color="green" />
            <StatusCard title="Email" provider="SMTP" enabled={status?.email?.enabled} icon={Mail}
              detail={status?.email?.enabled ? `Via ${status.email.host}` : 'Configure SMTP credentials'} color="blue" />
            <StatusCard title="Sitemap" provider="Auto-generated" enabled={true} icon={Globe}
              detail={`${sitemapPreview?.total_urls || 0} URLs indexed`} color="purple" />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><FileText size={18} /> Notification Triggers</h3>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              {[
                { event: 'New Order Placed', channels: 'Email + WhatsApp', auto: true },
                { event: 'Order Status Changed', channels: 'Email + WhatsApp', auto: true },
                { event: 'New Booking', channels: 'Email + WhatsApp', auto: true },
                { event: 'Booking Status Changed', channels: 'Email + WhatsApp', auto: true },
                { event: 'New Registration', channels: 'WhatsApp', auto: true },
                { event: 'Cart Abandoned', channels: 'Email', auto: false }
              ].map((t, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{t.event}</p>
                    <p className="text-xs text-gray-500">{t.channels}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${t.auto ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                    {t.auto ? 'Auto' : 'Manual'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-2">Recent Activity</h3>
            {logs.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No notifications sent yet. They'll appear here when orders or bookings are made.</p>
            ) : (
              <div className="space-y-2">
                {logs.slice(0, 5).map((log, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                    <div className="flex items-center gap-2">
                      {log.channels?.whatsapp && <MessageSquare size={14} className="text-green-500" />}
                      {log.channels?.email && <Mail size={14} className="text-blue-500" />}
                      <span className="font-medium">{log.type?.replace(/_/g, ' ')}</span>
                      <span className="text-gray-400">#{log.ref_id}</span>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Tab */}
      {activeTab === 'whatsapp' && (
        <div className="space-y-4">
          <div className={`rounded-xl p-5 shadow-sm border-l-4 ${status?.whatsapp?.enabled ? 'bg-white border-green-500' : 'bg-white border-yellow-500'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="text-green-600" size={22} />
                <h3 className="font-semibold text-gray-900">AiSensy WhatsApp Integration</h3>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${status?.whatsapp?.enabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {status?.whatsapp?.enabled ? 'Connected' : 'Setup Required'}
              </span>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {status?.whatsapp?.enabled
                ? 'API key is configured. Create templates in AiSensy dashboard and set up campaigns to start sending.'
                : 'Add AISENSY_API_KEY to your .env file to enable WhatsApp notifications.'}
            </p>
            <a href="https://app.aisensy.com" target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:text-green-800 flex items-center gap-1">
              <ExternalLink size={14} /> Open AiSensy Dashboard
            </a>
          </div>

          {/* Templates */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-3">WhatsApp Templates</h3>
            <p className="text-sm text-gray-500 mb-4">Create these templates in your AiSensy dashboard. The campaign name must match exactly.</p>
            <div className="space-y-3">
              {templates?.whatsapp_templates?.map((t, i) => (
                <TemplateCard key={i} template={t} />
              ))}
            </div>
          </div>

          {/* Setup Guide */}
          <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2"><AlertTriangle size={18} /> Setup Guide</h3>
            <ol className="text-sm text-blue-800 space-y-1.5">
              {templates?.setup_guide?.steps?.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>

          {/* Test WhatsApp */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Play size={18} /> Test WhatsApp</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Phone Number</label>
                <input type="tel" value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="919887068888"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="test-wa-phone" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Name</label>
                <input type="text" value={testName} onChange={e => setTestName(e.target.value)} placeholder="Test User"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="test-wa-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Template</label>
                <select value={testTemplate} onChange={e => setTestTemplate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="test-wa-template">
                  {templates?.whatsapp_templates?.map(t => (
                    <option key={t.campaign_name} value={t.campaign_name}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={sendTestWhatsApp} disabled={sending} data-testid="test-wa-send-btn"
                  className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2">
                  {sending ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />} Send Test
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Tab */}
      {activeTab === 'email' && (
        <div className="space-y-4">
          <div className={`rounded-xl p-5 shadow-sm border-l-4 ${status?.email?.enabled ? 'bg-white border-blue-500' : 'bg-white border-yellow-500'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Mail className="text-blue-600" size={22} />
                <h3 className="font-semibold text-gray-900">Brevo Email Service</h3>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${status?.email?.enabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {status?.email?.enabled ? `Connected - ${status.email?.provider || 'SMTP'}` : 'Not Connected'}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {status?.email?.enabled
                ? `Sending from ${status.email.from_email} via ${status.email.host}`
                : 'Email SMTP is not configured.'}
            </p>
          </div>

          {/* Email Templates with Preview */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Email Templates</h3>
            <div className="space-y-3">
              {(templates?.email_templates || []).map((t, i) => (
                <EmailTemplateCard key={i} template={t} />
              ))}
            </div>
          </div>

          {/* Test Email Sender */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Play size={18} /> Send Test Email</h3>
            <div className="grid md:grid-cols-4 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Recipient Email</label>
                <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="your@email.com"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="test-email-address" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Recipient Name</label>
                <input type="text" value={testName} onChange={e => setTestName(e.target.value)} placeholder="Rajesh Kumar"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="test-email-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Template</label>
                <select value={testEmailTemplate} onChange={e => setTestEmailTemplate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="test-email-template">
                  {(templates?.email_templates || []).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={sendTestEmail} disabled={sending} data-testid="test-email-send-btn"
                  className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2">
                  {sending ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />} Send Test
                </button>
              </div>
            </div>
            {!status?.email?.enabled && (
              <p className="mt-3 text-xs text-yellow-600 bg-yellow-50 p-2 rounded-lg">SMTP not configured. Test emails won't be delivered.</p>
            )}
          </div>
        </div>
      )}

      {/* Sitemap Tab */}
      {activeTab === 'sitemap' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-purple-500">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Globe className="text-purple-600" size={22} />
                <h3 className="font-semibold text-gray-900">Sitemap Generator</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{sitemapPreview?.total_urls || 0} URLs</span>
                <a href={`${BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noopener noreferrer" data-testid="view-sitemap-btn"
                  className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs font-medium flex items-center gap-1">
                  <ExternalLink size={14} /> View XML
                </a>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Your sitemap is auto-generated from CMS pages, products, and categories. Submit it to Google Search Console for better indexing.
            </p>
            <div className="bg-gray-50 p-3 rounded-lg text-xs font-mono text-gray-600">
              <p>Sitemap URL: https://www.rodekho.in/api/sitemap.xml</p>
              <p className="mt-1">Submit to: <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Google Search Console</a></p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Indexed URLs</h3>
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr><th className="text-left p-2 font-medium text-gray-600">URL</th><th className="text-left p-2 font-medium text-gray-600">Type</th><th className="text-left p-2 font-medium text-gray-600">Status</th></tr>
                </thead>
                <tbody>
                  {sitemapPreview?.urls?.map((u, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="p-2 text-blue-600 truncate max-w-xs">{u.url}</td>
                      <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded-full ${u.type === 'product' ? 'bg-blue-100 text-blue-700' : u.type === 'category' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{u.type}</span></td>
                      <td className="p-2">{u.no_index ? <span className="text-xs text-red-500">No Index</span> : <CheckCircle size={14} className="text-green-500" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Notification Log ({totalLogs} total)</h3>
            <button onClick={fetchLogs} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"><RefreshCw size={14} /> Refresh</button>
          </div>
          {logs.length === 0 ? (
            <p className="text-sm text-gray-400 italic py-8 text-center">No notifications sent yet.</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      {log.channels?.whatsapp && <MessageSquare size={14} className="text-green-500" />}
                      {log.channels?.email && <Mail size={14} className="text-blue-500" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{log.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                      <p className="text-xs text-gray-400">
                        {log.phone && `Phone: ${log.phone}`}{log.phone && log.email && ' | '}{log.email && `Email: ${log.email}`}
                        {log.ref_id && ` | Ref: #${log.ref_id}`}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusCard({ title, provider, enabled, icon: Icon, detail, color }) {
  const colors = {
    green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600' }
  };
  const c = colors[color] || colors.blue;
  return (
    <div className={`${c.bg} rounded-xl p-4 border ${c.border}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={c.icon} size={24} />
        {enabled ? <CheckCircle size={18} className="text-green-500" /> : <AlertTriangle size={18} className="text-yellow-500" />}
      </div>
      <h4 className="font-semibold text-gray-900">{title}</h4>
      <p className="text-xs text-gray-500">{provider}</p>
      <p className="text-xs text-gray-600 mt-1">{detail}</p>
    </div>
  );
}

function TemplateCard({ template }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 hover:bg-gray-50 text-left">
        <div>
          <p className="font-medium text-gray-900 text-sm">{template.name}</p>
          <p className="text-xs text-gray-500">Campaign: <code className="bg-gray-100 px-1 rounded">{template.campaign_name}</code> | Trigger: {template.trigger}</p>
        </div>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {open && (
        <div className="px-3 pb-3 border-t border-gray-100 pt-2">
          <p className="text-xs font-medium text-gray-600 mb-1">Template Sample:</p>
          <div className="bg-green-50 p-3 rounded-lg text-sm text-gray-800 border border-green-200">{template.sample}</div>
          <p className="text-xs font-medium text-gray-600 mt-2 mb-1">Parameters:</p>
          <div className="flex flex-wrap gap-1">
            {template.params?.map((p, i) => (
              <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">{`{{${i + 1}}}`} = {p}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmailTemplateCard({ template }) {
  const [showPreview, setShowPreview] = useState(false);
  const previewUrl = `${BACKEND_URL}/api/admin/email/preview/${template.id}`;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between p-4 hover:bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Mail size={18} className="text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{template.name}</p>
            <p className="text-xs text-gray-500">Trigger: {template.trigger}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium">Ready</span>
          <button onClick={() => setShowPreview(!showPreview)} data-testid={`preview-${template.id}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1.5 transition-colors ${
              showPreview ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            <Eye size={14} /> {showPreview ? 'Hide' : 'Preview'}
          </button>
          <a href={previewUrl} target="_blank" rel="noopener noreferrer"
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-1.5">
            <ExternalLink size={14} /> Full View
          </a>
        </div>
      </div>
      {showPreview && (
        <div className="border-t border-gray-200 bg-gray-100 p-4">
          <div className="bg-white rounded-lg shadow-inner overflow-hidden" style={{ maxHeight: 400, overflowY: 'auto' }}>
            <iframe src={previewUrl} title={`Preview: ${template.name}`} className="w-full border-0" style={{ height: 380 }} />
          </div>
        </div>
      )}
    </div>
  );
}

